/*
<summary>
  This functional component "ChangePasswordForm" provides form to change the password of the selected user.
  Developer:Aashish Singh, Created Date:29-August-2023
</summary>
<returns>Returns JSX</returns>
*/
import { Field, Form, Formik } from 'formik';
import { useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import validAllowedCharacters from '../../../constants/valid-character-password';
import { IChangePassword } from '../../../models/forms/IChangePassword';
import LoaderButton from '../../../shared-components/button/loader-button';
import FormikFormInput from '../../../shared-components/FormikFormHandlers/FormikFormInput';
import { UserChangePasswordValidateSchema } from '../../../shared-components/FormikFormHandlers/FormikValidationSchema';
import Label from '../../../shared-components/label/label';
import { formatMessage } from '../../../translations/format-message';

interface IProps {
  id: number;
  submitHandler: (data: any) => void;
  onModalClosed: () => void;
  isLoading: boolean;
  initialValues: IChangePassword;
}

const ChangePasswordForm = (props: IProps) => {
  const [showHidePassword, setShowHidePassword] = useState(false);
  const [showHideConfirmPassword, setShowHideConfirmPassword] = useState(false);

  return (
    <>
      <Modal
        className="changePasswordModal fancy-modal"
        show={true}
        centered
        onHide={() => {}}
      >
        <Modal.Header>
          <Modal.Title>
            <button
              title={formatMessage('close')}
              onClick={props.onModalClosed}
              disabled={props.isLoading}
              className="Crossicons"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="23"
                height="23"
                fill="currentColor"
                className="bi bi-x"
                viewBox="0 0 16 16"
              >
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
              </svg>
            </button>
            <FormattedMessage id="change_password" />
          </Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={props.initialValues}
          enableReinitialize
          validationSchema={UserChangePasswordValidateSchema}
          validateOnBlur={false}
          onSubmit={async (values) => {
            props.submitHandler(values);
          }}
        >
          <Form className="change_password-form">
            <Modal.Body>
              <div className="col-md-12 col-12 password-icon">
                <div className="form-group">
                  <Label
                    className="label-style"
                    required={true}
                    label="new_password"
                  />
                  <div>
                    <Field
                      data-testid="testPassword"
                      className="form-control"
                      name="NewPassword"
                      autoComplete="new-password"
                      type={showHidePassword ? 'text' : 'password'}
                      invalidcharacter={[' ']}
                      validcharacter={validAllowedCharacters}
                      placeholder=""
                      as={FormikFormInput}
                    />
                    {showHidePassword ? (
                      <svg
                        type="button"
                        onClick={() => {
                          setShowHidePassword(!showHidePassword);
                        }}
                        viewBox="0 0 24 24"
                        width="20"
                        height="20"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="showHidePass css-i6dzq1 modalBox"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle
                          cx="12"
                          cy="12"
                          r="3"
                        ></circle>
                      </svg>
                    ) : (
                      <svg
                        type="button"
                        onClick={() => {
                          setShowHidePassword(!showHidePassword);
                        }}
                        viewBox="0 0 24 24"
                        width="20"
                        height="20"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="showHidePass css-i6dzq1 modalBox"
                      >
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                        <line
                          x1="1"
                          y1="1"
                          x2="23"
                          y2="23"
                        ></line>
                      </svg>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-md-12 col-12 password-icon">
                <div className="form-group">
                  <Label
                    className="label-style"
                    required={true}
                    label="confirm_password"
                  />
                  <div>
                    <Field
                      data-testid="testConfirmPassword"
                      className="form-control"
                      name="ConfirmPassword"
                      autoComplete="confirm-new-password"
                      type={showHideConfirmPassword ? 'text' : 'password'}
                      invalidcharacter={[' ']}
                      validcharacter={validAllowedCharacters}
                      placeholder=""
                      as={FormikFormInput}
                    />
                    {showHideConfirmPassword ? (
                      <svg
                        type="button"
                        onClick={() => {
                          setShowHideConfirmPassword(!showHideConfirmPassword);
                        }}
                        viewBox="0 0 24 24"
                        width="20"
                        height="20"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="showHidePass css-i6dzq1 modalBox"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle
                          cx="12"
                          cy="12"
                          r="3"
                        ></circle>
                      </svg>
                    ) : (
                      <svg
                        type="button"
                        onClick={() => {
                          setShowHideConfirmPassword(!showHideConfirmPassword);
                        }}
                        viewBox="0 0 24 24"
                        width="20"
                        height="20"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="showHidePass css-i6dzq1 modalBox"
                      >
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                        <line
                          x1="1"
                          y1="1"
                          x2="23"
                          y2="23"
                        ></line>
                      </svg>
                    )}
                  </div>
                </div>
              </div>
            </Modal.Body>
            <div className="modal-footer">
              <div className="form-group row g-0 tab-action">
                <div className="col-12">
                  <Button
                    variant="light-danger"
                    type="button"
                    disabled={props.isLoading}
                    onClick={props.onModalClosed}
                  >
                    <FormattedMessage id="cancel" />
                  </Button>
                  <LoaderButton
                    type="submit"
                    isLoading={props.isLoading}
                    text="button_save"
                  />
                </div>
              </div>
            </div>
          </Form>
        </Formik>
      </Modal>
    </>
  );
};

export default ChangePasswordForm;
